import styled, { createGlobalStyle } from 'styled-components';

import { CustomThemeToken } from '@/theme';

import { StyledProps } from './DetailsWidgetSlider.types';
import {
  getLimitedRelativePosition,
  ANIMATION_SPEED,
  getBeforeBackgroundColor,
  getAfterBackgroundColor,
} from './DetailsWidgetSlider.utils';

export const Point = styled.div<StyledProps>`
  position: absolute;
  text-align: center;
  transform: translateX(-50%);
  left: ${({ $value, $min, $max, $addSideGaps }) => getLimitedRelativePosition($value, $min, $max, $addSideGaps)}%;

  .pipe {
    display: inline-block;
    width: ${({ $highlighted }) => ($highlighted ? '3px' : '2px')};
    height: 10px;
    background-color: ${({ theme: { token } }) => token.colorText};
  }

  .subsequent-label {
    position: relative;
    margin-top: 25px;
  }

  .target {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid ${({ theme: { token } }) => token.colorBgElevated};
    background-color: ${({ theme: { token } }) => token.colorCyanTextHover};
    box-sizing: content-box;
  }

  .target-tooltip {
    background-color: ${({ theme: { token } }) => token.colorCyanTextHover};
  }

  .point-value {
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    color: ${({ theme: { token }, $highlighted }) => ($highlighted ? token.colorText : token.colorTextSecondary)};
  }

  .label {
    color: ${({ theme: { token }, $highlighted }) => ($highlighted ? token.colorText : token.colorTextSecondary)};
  }
`;

export const Container = styled.div<StyledProps & { $isInMaintenance: boolean }>`
    height: 100%;
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    border-radius: 2px;
      
    .title-suffix {
      color: ${({ theme: { token } }) => token.colorText};
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      text-transform: uppercase;
      margin-right: ${({ theme: { token } }) => token.marginXXS}px; 
    }
  
    .title {
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      font-size: ${({ theme: { token } }) => token.fontSize}px;
    }
    .title-wrapper-tier {
      display:flex;
      justify-content:space-between;
    }
  
    .target-value {
      ${({ $unit }) => $unit !== 'ppm' && `padding-top: ${({ theme: { token } }) => token.marginSM}px`};
      font-size:${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorText};
      padding-bottom: ${({ theme: { token } }) => token.paddindXS}px;
  
      .target-value-value, .target-value-unit {
        color: ${({ theme: { token } }) => token.colorCyanTextHover};

      }
    }
  
    .target-value-hidden {
      visibility: hidden;
    }
  
    .lock-wrapper {
      margin-right: -${({ theme: { token } }) => token.marginMD}px;
      margin-left: ${({ theme: { token } }) => token.margin}px;
      margin-top: -${({ theme: { token } }) => token.marginSM}px;
      
    }
  
    .slider-container {
      position: relative;
      margin: auto;
      width: 100%;
      height: 100%;
  
      .value-container {
        display: flex;
        position: relative;
      }
  
      .value-arrow-block {
        display: flex;
        position: relative;
        flex-direction: column;
        align-items: center;
        left: ${({ $value, $min, $max, $addSideGaps }) =>
          getLimitedRelativePosition($value, $min, $max, $addSideGaps)}%;
        transform: translateX(-50%);
        transition: all ${ANIMATION_SPEED}s ease-out;
  
        .triangle {
          transform: rotate(180deg);
        }
      }
  
      .value {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        ${({ $value, $min, $max, $addSideGaps, theme: { token } }) =>
          getLimitedRelativePosition($value, $min, $max, $addSideGaps) < 0
            ? `padding-left: ${`${$value}`.length * token.paddingSM}px;`
            : getLimitedRelativePosition($value, $min, $max, $addSideGaps) > 100
            ? `padding-right: ${`${$value}`.length * token.paddingSM}px;`
            : ''};
  
        .value-content {
          font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
          color: ${({ theme: { token } }) => token.colorText};
        }
  
        .units-sign {
          position: relative;
          color: ${({ theme: { token } }) => token.colorTextSecondary};
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          margin-left: ${({ theme: { token } }) => token.marginXXS}px;
        }
      }
  
      .slider {
        height: 3px;
        width: 100%;
        background-color: ${({ theme: { token } }) => token.colorSplit};
        display: flex;
        justify-content: flex-start;
  
        .grey-filler {
          flex-grow: 1;
        }
  
        .filler {
          background-color: ${({ $highlightSlider, theme: { token } }) =>
            $highlightSlider ? token.colorPrimary : token.colorBorder};
          height: 100%;
          width: ${({ $value, $min, $max, $addSideGaps }) =>
            getLimitedRelativePosition($value, $min, $max, $addSideGaps) || 0}%;
          transition: all ${ANIMATION_SPEED}s ease-out;
        }
  
        &:before {
          
          height: 100%;
          content: '';
          background-color: ${({ $value, $min, $max, $highlightSlider, theme: { token } }) =>
            getBeforeBackgroundColor($value, $min, $max, $highlightSlider, token)};
        }
  
        &:after {
          margin-right: -10%;
          height: 3px;
          right: 0;
          content: '';
          background-color: ${({ $value, $min, $max, $highlightSlider, theme: { token } }) =>
            getAfterBackgroundColor($value, $min, $max, $highlightSlider, token)};
        }
  
  
      }
    }
  
    .points {
      display: flex;
      justify-content: space-between;
      position: relative;
      top: -10px;
  `;

export const TargetTooltipPopoverStyle = createGlobalStyle<{ theme: { token: CustomThemeToken } }>`
    .ant-popover.target-tooltip {
      .ant-popover-inner {
        padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;
        background-color: ${({ theme: { token } }) => token.colorCyanTextHover};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }
  
      .ant-popover-arrow::before {
        background-color: ${({ theme: { token } }) => token.colorCyanTextHover};
      }
    }
  `;
